/**
 * Active producers / injectors per year — X-axis synced with FieldProductionChart.
 */
import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AxisSideLabel,
  AxisSideSpacer,
  FIELD_CHART_MARGIN,
  FIELD_COLORS,
  FIELD_Y_AXIS,
  RightTickBandSpacer,
  formatYTick,
  niceCeil,
} from '@/components/Scenarios/FieldProductionChart';
import type { MbFieldSeriesPoint } from '@/utils/materialBalanceFieldAggregate';

export interface ActiveWellsBarChartProps {
  field: MbFieldSeriesPoint[];
  xDomain: [number, number] | null;
  height?: number;
}

export const ActiveWellsBarChart: React.FC<ActiveWellsBarChartProps> = ({
  field,
  xDomain,
  height = 220,
}) => {
  const data = useMemo(
    () =>
      field.map((r) => ({
        year: r.year,
        producers: r.n_prod,
        injectors: r.n_inj,
      })),
    [field],
  );

  const wellsMax = useMemo(() => {
    let m = 0;
    for (const r of field) m = Math.max(m, r.n_prod, r.n_inj, r.n_prod + r.n_inj);
    return niceCeil(m * 1.08 || 1);
  }, [field]);

  if (!data.length) {
    return (
      <Box
        sx={{
          height,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1,
          boxSizing: 'border-box',
          width: '100%',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Active wells — no series yet.
        </Typography>
      </Box>
    );
  }

  const chartH = height - 28;

  return (
    <Box
      sx={{
        minHeight: height,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 0.75,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <Typography variant="caption" fontWeight={600} sx={{ display: 'block', px: 0.25, mb: 0.5 }}>
        Active wells
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          width: '100%',
          height: chartH,
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <AxisSideLabel side="left">wells</AxisSideLabel>
        <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ ...FIELD_CHART_MARGIN }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.35} />
              <XAxis
                dataKey="year"
                type="number"
                domain={xDomain || ['dataMin', 'dataMax']}
                allowDataOverflow
                tick={{ fontSize: 10 }}
              />
              <YAxis
                yAxisId="wells"
                type="number"
                domain={[0, wellsMax]}
                tick={{ fontSize: 10 }}
                tickFormatter={formatYTick}
                width={FIELD_Y_AXIS.left}
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (typeof value !== 'number') return [String(value ?? ''), String(name ?? '')];
                  return [String(Math.round(value)), String(name ?? '')];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
              <Bar
                yAxisId="wells"
                dataKey="producers"
                name="producers"
                fill={FIELD_COLORS.prodWells}
                isAnimationActive={false}
              />
              <Bar
                yAxisId="wells"
                dataKey="injectors"
                name="injectors"
                fill={FIELD_COLORS.injWells}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        {/* Match FieldProductionChart: WC band · Np band · WC caption · Np caption */}
        <RightTickBandSpacer width={FIELD_Y_AXIS.rightWc} />
        <RightTickBandSpacer width={FIELD_Y_AXIS.rightNp} />
        <AxisSideSpacer />
        <AxisSideSpacer />
      </Box>
    </Box>
  );
};
